import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "card",
    "uploadArea",
    "input",
    "dropZone",
    "progressArea",
    "filename",
    "filesize",
    "badge",
    "status",
    "spinner",
    "error",
  ];

  connect() {
    this.selectedKind = null;
  }

  // 「登録する」「差し替え」から呼ばれ、対象カードを選択状態にしてアップロードエリアを表示する
  select(event) {
    event.preventDefault();

    const card = event.currentTarget.closest(
      "[data-profiles--document-upload-target='card']",
    );
    if (!card) return;

    this.selectedKind = card.dataset.documentKind;
    this.cardTargets.forEach((element) => {
      element.classList.toggle("is-selected", element === card);
    });

    this.hideError();
    this.resetProgressUI();
    this.uploadAreaTarget.style.display = "block";
  }

  cancel(event) {
    event.preventDefault();
    this.clearSelection();
  }

  clearSelection() {
    this.selectedKind = null;
    this.cardTargets.forEach((element) => {
      element.classList.remove("is-selected");
    });
    this.resetProgressUI();
    this.uploadAreaTarget.style.display = "none";
  }

  handleClick(event) {
    event.preventDefault();
    this.inputTarget.click();
  }

  handleFileSelect(event) {
    const files = event.target.files;
    if (files.length > 0) {
      this.uploadFile(files[0]);
    }
  }

  handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    this.dropZoneTarget.classList.add("drag-over");
  }

  handleDragEnter(event) {
    event.preventDefault();
    event.stopPropagation();
    this.dropZoneTarget.classList.add("drag-over");
  }

  handleDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();

    // ドロップゾーンから完全に離れた場合のみクラスを削除
    if (!this.dropZoneTarget.contains(event.relatedTarget)) {
      this.dropZoneTarget.classList.remove("drag-over");
    }
  }

  handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    this.dropZoneTarget.classList.remove("drag-over");

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      this.uploadFile(files[0]);
    }
  }

  uploadFile(file) {
    if (!this.selectedKind) {
      this.showError("履歴書か職務経歴書のどちらを登録するか選択してください。");
      return;
    }

    this.showProgressUI(file);

    const formData = new FormData();
    formData.append("document_kind", this.selectedKind);
    formData.append("file", file);

    fetch("/mypage/documents", {
      method: "POST",
      body: formData,
      headers: this.csrfHeaders(),
    })
      .then((response) => {
        if (response.ok) {
          this.updateStatus("アップロード完了");
          this.hideSpinner();
          window.location.reload();
        } else {
          response
            .json()
            .then((data) => {
              this.showError(data.error || "エラーが発生しました");
            })
            .catch(() => {
              this.showError("エラーが発生しました");
            });
        }
      })
      .catch(() => {
        this.showError("エラーが発生しました");
      });
  }

  // CSRFトークンのmetaタグはforgery protectionが無効な環境では出力されない
  csrfHeaders() {
    const token = document.querySelector('[name="csrf-token"]');
    return token ? { "X-CSRF-Token": token.content } : {};
  }

  showProgressUI(file) {
    this.dropZoneTarget.style.display = "none";
    this.progressAreaTarget.style.display = "block";
    this.spinnerTarget.style.display = "";

    this.filenameTarget.textContent = file.name;
    this.filesizeTarget.textContent = this.formatFileSize(file.size);
    this.badgeTarget.textContent = this.selectedLabel();
    this.statusTarget.textContent = "アップロード中...";
  }

  resetProgressUI() {
    this.progressAreaTarget.style.display = "none";
    this.dropZoneTarget.style.display = "";
    this.spinnerTarget.style.display = "";
    this.inputTarget.value = "";
  }

  selectedLabel() {
    const card = this.cardTargets.find(
      (element) => element.dataset.documentKind === this.selectedKind,
    );
    return card ? card.dataset.documentLabel : "";
  }

  updateStatus(status) {
    this.statusTarget.textContent = status;
  }

  hideSpinner() {
    this.spinnerTarget.style.display = "none";
  }

  showError(message) {
    this.resetProgressUI();

    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.style.display = "block";
  }

  hideError() {
    if (!this.hasErrorTarget) return;
    this.errorTarget.style.display = "none";
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i];
  }
}
